//
import './index.less';
import {
  Box,
  ButtonBase,
  Divider,
  Flex,
  Icon,
  Image,
  Link as TonicLint,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import {
  CollapseLeftIcon,
  CollapseRightIcon,
  ExternalLinkIcon,
} from '@tonic-one/react-icons';
import { Layout, Menu, MenuProps } from 'antd';

import logo from '../../assets/image/logo.svg';
import addaLogoMini from '../../assets/image/white-label/cyber-eye.svg';
import logoMini from '../../assets/image/logo-mini.svg';
import OnlineHelp from './OnlineHelp';
import { useTranslation } from 'react-i18next';
import { Link, matchRoutes, useLocation } from 'react-router-dom';
import { PermissionAction, PermissionNameEnum } from '@Types/permissions';
import routerConfig from '../../routerConfig';
import common, { isInUIC } from '../../utils/common';
import { IS_UAE } from '@Utils/const';
import {
  AdministrationSvg,
  DashboardSvg,
  LogSvg,
  OperationsSvg,
  PolicySvg,
  ReportSvg,
  XSvg,
} from './SiderIcon';
import { usePermissionStore } from '@Models/usePermissionModel';
import { useGlobalConfigStore } from '@Models/global-configs/useGlobalConfigModel';
import { useSiderWidthStore } from '@Models/useSiderWidthModel';
import { useWhiteLabelStore } from '@Models/useWhiteLabelModel';
import { useLayoutStore } from '../../models/useLayoutModel';

const IconMap = {
  dashboard: DashboardSvg,
  logs: LogSvg,
  quarantine: OperationsSvg,
  report: ReportSvg,
  policy: PolicySvg,
  administration: AdministrationSvg,
  visionOne: XSvg,
};

const PermissionMap = {
  dashboard: PermissionNameEnum.dashboard,
  logs: PermissionNameEnum.log,
  quarantine: PermissionNameEnum.quarantine,
  report: PermissionNameEnum.report,
  policy: PermissionNameEnum.atpPolicy,
  administration: PermissionNameEnum.administrator,
};

const i18nKeyMap = {
  dashboard: 'side.group.dashboard',
  logs: 'side.group.log',
  quarantine: 'side.group.operations',
  report: 'side.group.report',
  policy: 'side.group.policy',
  administration: 'side.group.administrator',
  visionOne: 'side.group.detectionAndResponse',
};

export default function CasSider() {
  const currentLocation = useLocation();
  const { bannerHeight } = useLayoutStore();
  const siderKey: string =
    matchRoutes(
      common.flattenRoutes(routerConfig),
      currentLocation
    )?.[0].route.path.slice(1) || 'undefined';

  if (isInUIC()) {
    return <></>;
  }

  return <SideMenu siderKey={siderKey} mt={`${bannerHeight}px`} />;
}

function SideMenu({ siderKey, mt }: { siderKey: string; mt: string }) {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode: 'dark' }); // always use dark mode
  const { collapsed, setSiderCollapsed } = useSiderWidthStore();

  type MenuItem = Required<MenuProps>['items'][number];
  const { t } = useTranslation();
  const globalConfigModel = useGlobalConfigStore();
  const permissionModel = usePermissionStore();
  const whiteLabelModel = useWhiteLabelStore();
  function getItem(
    label: React.ReactNode,
    key: React.Key,
    icon?: React.ReactNode,
    children?: MenuItem[],
    type?: 'group'
  ) {
    return {
      key,
      icon,
      children,
      label,
      type,
    } as MenuItem;
  }

  const iconColor = collapsed
    ? 'rgba(255, 255, 255, .47)'
    : 'rgba(255, 255, 255, 1.0)';

  const getColor = (sideItem: string) => {
    const color = siderKey.indexOf(sideItem) === 0 ? '#F24C4F' : iconColor;
    return color;
  };

  const tipsColor = (sideItem: string) => {
    if (siderKey.indexOf(sideItem) === 0) {
      return '#F24C4F';
    }
    if (!collapsed) {
      return iconColor;
    }
    return colorMode === 'dark'
      ? 'rgba(255, 255, 255, .47)'
      : 'rgba(0, 0, 0, .47)';
  };

  const menuItems = [
    'dashboard',
    'policy',
    'logs',
    'quarantine',
    'report',
    'administration',
    'visionOne',
  ].map((sideItem) => {
    if (sideItem === 'policy') {
      const color = getColor('policy');
      const SideIcon = IconMap['policy'];
      return globalConfigModel.globalConfig.isShowSideMenu?.policy &&
        permissionModel.permissions?.atpPolicy
          .concat(permissionModel.permissions?.dlpPolicy)
          .concat(permissionModel.permissions?.administrator)
          .includes(PermissionAction.ViewOnly)
        ? getItem(
            <Link to="/policy">
              <Text fontSize="14px" color={tipsColor('policy')}>
                {t('side.group.policy')}
              </Text>
            </Link>,
            'policy',
            <SideIcon
              style={{
                flexShrink: 0,
                fontSize: collapsed ? '24px' : '16px',
                color,
              }}
            />
          )
        : null;
    } else if (sideItem === 'visionOne') {
      const color = getColor('visionOne');
      const SideIcon = IconMap['visionOne'];
      return globalConfigModel.globalConfig.isShowSideMenu?.visionOne
        ? getItem(
            <TonicLint
              target="_blank"
              href="https://portal-xdr.visionone.trendmicro.com/#/workbench"
            >
              <Text fontSize="14px" color={tipsColor('visionOne')}>
                {t('side.group.detectionAndResponse')}
              </Text>
              <Space width="2x" />
              <Icon color="#FFFFFFEB" as={ExternalLinkIcon} />
            </TonicLint>,
            'detectionAndResponse',
            <SideIcon
              style={{
                flexShrink: 0,
                fontSize: collapsed ? '24px' : '16px',
                color,
              }}
            />
          )
        : null;
    } else {
      const _sideItem = sideItem as
        | 'dashboard'
        | 'logs'
        | 'quarantine'
        | 'report'
        | 'administration';
      const SideIcon = IconMap[_sideItem];
      const color = getColor(_sideItem);
      return globalConfigModel.globalConfig.isShowSideMenu?.[_sideItem] &&
        permissionModel.permissions?.[PermissionMap[_sideItem]].includes(
          PermissionAction.ViewOnly
        )
        ? getItem(
            <Link to={`/${_sideItem}`}>
              <Text fontSize="14px" color={tipsColor(_sideItem)}>
                {_sideItem === 'quarantine'
                  ? t(`side.group.operations`)
                  : t(`${i18nKeyMap[_sideItem]}`)}
              </Text>
            </Link>,
            _sideItem,
            <SideIcon
              style={{
                flexShrink: 0,
                fontSize: collapsed ? '24px' : '16px',
                color,
              }}
            />
          )
        : null;
    }
  });

  return (
    <Layout.Sider width={280} collapsedWidth={64} collapsed={collapsed}>
      <Flex height={`calc(100vh - ${mt})`} direction="column">
        <Flex mx="18px" height="48px" direction="row" align="center">
          {IS_UAE ? (
            collapsed ? (
              <Image src={addaLogoMini} height={28} mt="4px" />
            ) : (
              <>
                <Image src={addaLogoMini} height={28} mt="4px" />
                <Text
                  fontSize="20px"
                  fontWeight="400"
                  ml={8}
                  color={colorStyle.color.primary}
                >
                  Cyber Eye
                </Text>
              </>
            )
          ) : (
            !whiteLabelModel.whiteLabel && (
              <Image src={collapsed ? logoMini : logo} height={28} mt="4px" />
            )
          )}
        </Flex>
        <Menu
          className="menu-menu"
          mode="inline"
          theme="dark"
          defaultSelectedKeys={[siderKey]}
          selectedKeys={[siderKey]}
          items={menuItems}
        />
        <Box flex="1" />
        {collapsed && (
          <Divider
            orientation="horizontal"
            m="12px 16px"
            width={32}
            background="rgba(255, 255, 255, 0.12)"
          />
        )}

        {renderFooter(collapsed, setSiderCollapsed, colorStyle, iconColor)}
      </Flex>
    </Layout.Sider>
  );
}

function renderFooter(
  collapsed: boolean,
  setCollapsed: (collapsed: boolean) => void,
  colorStyle: any,
  color: string
) {
  return (
    <Flex
      background={collapsed ? 'none' : colorStyle.background.primary}
      direction={collapsed ? 'column' : 'row'}
      height={collapsed ? 96 : 48}
      align="center"
      justify={collapsed ? 'space-around' : 'space-between'}
    >
      <OnlineHelp collapsed={collapsed} />
      <ButtonBase
        className="icon icon-btn"
        onClick={setCollapsed.bind(null, !collapsed)}
      >
        {collapsed ? (
          <Icon as={CollapseRightIcon} color={color} />
        ) : (
          <Icon as={CollapseLeftIcon} color={color} />
        )}
      </ButtonBase>
    </Flex>
  );
}
